import { useState } from 'react';
import * as Dialog from '@radix-ui/react-dialog';
import { Check, X, Sparkles, Star } from 'lucide-react';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { Button } from '@/components/ui/Button';
import { Badge } from '@/components/ui/Badge';
import { apiClient } from '@/services/api';
import { formatCents, cn } from '@/utils/cn';
import type { Plan, BillingCycle } from '@/types/plan';

interface Props {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  currentPlanCode: string;
  currentTvCount: number;
  currentBillingCycle: BillingCycle;
  isTrialing: boolean;
}

const FEATURE_HIGHLIGHTS: Record<string, string> = {
  agent_integration: 'Agent ETL (ERP/balança)',
  realtime_price_sync: 'Preço em tempo real',
  multi_store: 'Multi-loja',
  dedicated_account_manager: 'Gerente de conta',
  api_access: 'API REST',
};

/** Modal pra trocar de plano. Mostra os 3 cards e confirma via activateSubscription. */
export function ChangePlanModal({
  open,
  onOpenChange,
  currentPlanCode,
  currentTvCount,
  currentBillingCycle,
  isTrialing,
}: Props) {
  const qc = useQueryClient();
  const [selected, setSelected] = useState<string>(currentPlanCode);
  const [cycle, setCycle] = useState<BillingCycle>(currentBillingCycle);

  const { data: plans = [] } = useQuery<Plan[]>({
    queryKey: ['plans'],
    queryFn: () => apiClient.getPlans().then(r => r.data as Plan[]),
  });

  const selectedPlan = plans.find(p => p.code === selected);

  // Usa PATCH dedicado: funciona em trial e em ativo. Não exige PaymentMethod.
  const changeMutation = useMutation({
    mutationFn: () => {
      if (!selectedPlan) throw new Error('Selecione um plano');
      return apiClient.updateSubscription({
        plan_id: selectedPlan.id,
        billing_cycle: cycle,
      });
    },
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['subscription'] });
      qc.invalidateQueries({ queryKey: ['invoices'] });
      onOpenChange(false);
    },
  });

  function previewTotalCents(p: Plan): number {
    const first = cycle === 'monthly' ? p.first_tv_monthly_cents : Math.round(p.first_tv_annual_cents / 12);
    const add = cycle === 'monthly' ? p.price_per_tv_monthly_cents : Math.round(p.price_per_tv_annual_cents / 12);
    return first + (currentTvCount - 1) * add;
  }

  return (
    <Dialog.Root open={open} onOpenChange={onOpenChange}>
      <Dialog.Portal>
        <Dialog.Overlay className="fixed inset-0 z-50 bg-black/60 backdrop-blur-sm" />
        <Dialog.Content className="fixed left-1/2 top-1/2 z-50 w-[95vw] max-w-5xl max-h-[90vh] overflow-y-auto -translate-x-1/2 -translate-y-1/2 rounded-xl bg-background p-6 md:p-8 shadow-2xl">
          <div className="flex items-start justify-between mb-2">
            <div>
              <Dialog.Title className="text-2xl font-bold">Mudar plano</Dialog.Title>
              <Dialog.Description className="text-sm text-muted-foreground mt-1">
                Você tem <strong>{currentTvCount} TV{currentTvCount > 1 ? 's' : ''}</strong>.
                O preço final considera 1ª TV (taxa base) + TVs adicionais a R$ 49,90 cada.
              </Dialog.Description>
            </div>
            <Dialog.Close asChild>
              <button className="rounded-md p-1 hover:bg-muted" aria-label="Fechar">
                <X className="h-5 w-5" />
              </button>
            </Dialog.Close>
          </div>

          {/* Toggle ciclo */}
          <div className="flex justify-center mb-6 mt-4">
            <div className="inline-flex rounded-lg border bg-muted p-1">
              <button
                onClick={() => setCycle('monthly')}
                className={cn(
                  'px-4 py-2 text-sm rounded-md transition-colors',
                  cycle === 'monthly' ? 'bg-background shadow-sm font-semibold' : 'text-muted-foreground'
                )}
              >Mensal</button>
              <button
                onClick={() => setCycle('annual')}
                className={cn(
                  'px-4 py-2 text-sm rounded-md transition-colors',
                  cycle === 'annual' ? 'bg-background shadow-sm font-semibold' : 'text-muted-foreground'
                )}
              >Anual <span className="text-emerald-600 font-semibold ml-1">-20%</span></button>
            </div>
          </div>

          {/* 3 cards de planos */}
          <div className="grid md:grid-cols-3 gap-4">
            {plans.map((p) => {
              const isCurrent = p.code === currentPlanCode;
              const isSelected = p.code === selected;
              const isPro = p.code === 'pro';
              const totalCents = previewTotalCents(p);
              const highlights = Object.entries(p.features || {})
                .filter(([k, v]) => v === true && FEATURE_HIGHLIGHTS[k])
                .map(([k]) => FEATURE_HIGHLIGHTS[k]);

              return (
                <button
                  key={p.id}
                  type="button"
                  onClick={() => setSelected(p.code)}
                  className={cn(
                    'relative text-left rounded-xl border-2 p-5 transition-all',
                    isSelected
                      ? 'border-brand-600 bg-brand-50/40 shadow-md'
                      : 'border-border hover:border-muted-foreground/30 bg-background',
                  )}
                >
                  {isPro && !isSelected && (
                    <div className="absolute -top-2 right-3 gradient-brand text-white px-2 py-0.5 rounded-full text-[10px] font-bold flex items-center gap-1">
                      <Star className="h-3 w-3" /> Popular
                    </div>
                  )}
                  {isSelected && (
                    <div className="absolute -top-2 right-3 bg-brand-600 text-white px-2 py-0.5 rounded-full text-[10px] font-bold flex items-center gap-1">
                      <Check className="h-3 w-3" /> Selecionado
                    </div>
                  )}
                  {isCurrent && (
                    <Badge variant="muted" className="absolute top-3 left-3 text-[10px]">Atual</Badge>
                  )}

                  <div className={cn('mt-4', isCurrent && 'pt-4')}>
                    <h3 className="text-lg font-bold">{p.name}</h3>
                    <p className="text-xs text-muted-foreground mt-0.5 line-clamp-2 h-8">{p.description}</p>
                  </div>

                  <div className="mt-4">
                    <div className="text-3xl font-bold">{formatCents(totalCents)}<span className="text-sm text-muted-foreground font-normal">/mês</span></div>
                    <div className="text-xs text-muted-foreground mt-1">
                      pra {currentTvCount} TV{currentTvCount > 1 ? 's' : ''}
                      {cycle === 'annual' && ' · cobrança anual'}
                    </div>
                  </div>

                  <ul className="mt-4 space-y-1.5 text-xs">
                    <li className="flex gap-1.5">
                      <Check className="h-3.5 w-3.5 text-emerald-500 shrink-0 mt-0.5" />
                      <span>{p.max_products == null ? 'Produtos ilimitados' : `${p.max_products.toLocaleString('pt-BR')} produtos`}</span>
                    </li>
                    <li className="flex gap-1.5">
                      <Check className="h-3.5 w-3.5 text-emerald-500 shrink-0 mt-0.5" />
                      <span>{(p.max_storage_mb / 1024).toLocaleString('pt-BR')} GB de mídia</span>
                    </li>
                    {highlights.slice(0, 3).map(h => (
                      <li key={h} className="flex gap-1.5 font-medium">
                        <Sparkles className="h-3.5 w-3.5 text-brand-600 shrink-0 mt-0.5" />
                        <span>{h}</span>
                      </li>
                    ))}
                  </ul>
                </button>
              );
            })}
          </div>

          {/* Resumo da mudança */}
          {selectedPlan && (
            <div className="mt-6 rounded-lg bg-muted p-4 flex flex-wrap items-center justify-between gap-3">
              <div>
                <div className="text-xs text-muted-foreground">Você vai pagar</div>
                <div className="text-2xl font-bold">{formatCents(previewTotalCents(selectedPlan))}/mês</div>
                <div className="text-xs text-muted-foreground">
                  Plano <strong>{selectedPlan.name}</strong> · {currentTvCount} TV{currentTvCount > 1 ? 's' : ''} · {cycle === 'monthly' ? 'Mensal' : 'Anual'}
                </div>
              </div>
              {isTrialing && (
                <Badge variant="warning" className="text-xs">
                  Você está em trial — a cobrança só começa após a ativação
                </Badge>
              )}
            </div>
          )}

          {changeMutation.isError && (
            <div className="mt-3 text-sm text-red-600">
              Erro ao mudar plano. Tente novamente em alguns segundos.
            </div>
          )}

          <div className="mt-6 flex gap-2 justify-end">
            <Button variant="outline" onClick={() => onOpenChange(false)}>Cancelar</Button>
            <Button
              variant="gradient"
              loading={changeMutation.isPending}
              disabled={selected === currentPlanCode && cycle === currentBillingCycle}
              onClick={() => changeMutation.mutate()}
            >
              Confirmar mudança
            </Button>
          </div>
        </Dialog.Content>
      </Dialog.Portal>
    </Dialog.Root>
  );
}
